import { EmployeeProfile } from "components/EmployeeProfile";
import { differenceInYears, format } from "date-fns";
import React from "react";
import Select, { GroupBase, MenuListProps, components } from "react-select";
import { useAppSelector } from "store/hooks";
import {
  XeroImportEmployee,
  selectAllEmployees,
  useUpdateEmployeeMutation,
  useXeroImportMutation,
} from "store/reducers";
import { useFetchOrganisationsQuery } from "store/reducers/organisation";

import { EmployeeTable } from "./employees_list";
import { Button } from "../components/button";
import { Modal } from "../components/modal";

type EmployeeOption = {
  value: string;
  label: string;
  employee: Employee;
  isDisabled: boolean;
};

const MenuList = (props: MenuListProps<EmployeeOption, false, GroupBase<EmployeeOption>>) => {
  return (
    <components.MenuList {...props}>
      <div className="p-2">
        <p>If the employee is already in RadPay (unlikely), this links to their record & timesheets.</p>
        <p>This only occurs if data was adjusted by a system administrator.</p>
        <p>
          <span className="text-danger">Red</span> means the employee is already linked to Xero, and linking will
          overwrite their existing data.
        </p>
      </div>
      {props.children}
    </components.MenuList>
  );
};

export function EmployeeImport({ e }: { e: XeroImportEmployee }) {
  const { data: orgStore } = useFetchOrganisationsQuery();

  const employees = useAppSelector(selectAllEmployees);

  const [xeroImport, xeroImportResult] = useXeroImportMutation();
  const [updateEmployee, updateEmployeeResult] = useUpdateEmployeeMutation();

  // Read employee from the store so edits are displayed after import
  const employeeFromStore = employees.find((e) => e.xero_EmployeeID === xeroImportResult.data?.xero_EmployeeID);
  const [selectedEmployee, setSelectedEmployee] = React.useState<EmployeeOption>();
  const [editingEmployeeId, setEditingEmployeeId] = React.useState<number>();

  const { employee, xero_tenantId } = e;

  async function onClickImport() {
    await xeroImport({
      EmployeeID: employee.employee_id,
      xero_tenantId,
    });
  }

  async function onClickLink() {
    if (!selectedEmployee) return;
    await updateEmployee({
      employeeId: selectedEmployee.employee.id,
      body: {
        EmployeeID: employee.employee_id,
        tenantId: xero_tenantId,
      },
    });
  }

  let org = orgStore?.organisations.find((o) => o.xero_tenantId === xero_tenantId);

  let employeeOptions = employees
    .map((e) => {
      return {
        value: String(e.id),
        label: e.name,
        employee: e,
      } as EmployeeOption;
    })
    .sort((a, b) => {
      // sort disabled to bottom
      return Number(Boolean(a.employee.xero_EmployeeID)) - Number(Boolean(b.employee.xero_EmployeeID));
    });

  return (
    <div key={employee.employee_id}>
      {!employeeFromStore && (
        <div>
          <div>
            {employee.first_name} {employee.last_name}
          </div>
          {employee.date_of_birth && (
            <div>
              {format(new Date(employee.date_of_birth), "dd/MM/yyyy")} (
              {differenceInYears(new Date(), new Date(employee.date_of_birth))})
            </div>
          )}
          <div>{org?.xero_tenantName}</div>
          <div className="flex flex-row items-center gap-3">
            <Button colour="accent" onClick={onClickImport} disabled={!xeroImportResult.isUninitialized}>
              Import
            </Button>
            <strong>OR</strong>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            {employeeOptions && (
              <div className="flex flex-row" style={{ width: 400 }}>
                <div className="flex-grow">
                  <Select
                    styles={{
                      option: (provided, state) => {
                        const opacity = state.isDisabled ? 0.5 : 1;
                        return {
                          ...provided,
                          color: state.data.employee.xero_EmployeeID ? "red" : undefined,
                          opacity,
                        };
                      },
                    }}
                    onChange={(e) => setSelectedEmployee(e as EmployeeOption)}
                    value={selectedEmployee}
                    options={employeeOptions}
                    isClearable
                    components={{ MenuList }}
                  />
                </div>
                <Button
                  className="ml-1"
                  colour="accent"
                  size="sm"
                  disabled={updateEmployeeResult.isLoading}
                  onClick={onClickLink}
                >
                  Link
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {employeeFromStore && (
        <EmployeeTable
          title={org?.xero_tenantName || "No organisation"}
          employees={[employeeFromStore]}
          onEdit={setEditingEmployeeId}
        />
      )}
      {editingEmployeeId != null && (
        <Modal isOpen dismissModal={() => setEditingEmployeeId(undefined)} title="Edit Employee">
          <EmployeeProfile employeeId={editingEmployeeId} onCancel={() => setEditingEmployeeId(undefined)} />
        </Modal>
      )}
    </div>
  );
}
